import HttpStatus from 'http-status-codes';
import jQuery from 'jquery';
import { useEffect, useReducer, useState } from 'react';

/*
jQuery.ajaxSetup({
  beforeSend: (xhr, options) => {
    xhr.setRequestHeader('X-Client', 'C2S-React');
    xhr.setRequestHeader('X-Client-Package', GIT_REVISION);
    if (options.contentType === 'application/json' && typeof options.data !== 'string') {
      options.data = JSON.stringify(options.data);
    }
    if (options.url.indexOf('?') < 0) {
      options.url += '?';
    }
    options.url += '&ajax=true';
  },
});
*/

const loginRedirectHandler = (jqXHR, _textStatus, _errorThrown) => {
  if (jqXHR.status === HttpStatus.FORBIDDEN && jqXHR.responseText === 'NotAuthenticated' && jsRoutes) {
    window.location.href = jsRoutes.controllers.Authentication.login(window.location.href).url;
  }
};

function setDefaults(args) {
  // Don't process data by default on json requests
  for (let i = 0; i < args.length; i++) {
    const settings = args[i];
    if (typeof settings === 'object') {
      if (typeof settings.processData === 'undefined' && settings.contentType === 'application/json') {
        args[i] = Object.assign({}, args[i], { processData: false });
      }
    }
  }
  return args;
}

export const ajax = (...args) => jQuery.ajax(...setDefaults(args)).fail(loginRedirectHandler);
export const get = (...args) => jQuery.get(...setDefaults(args)).fail(loginRedirectHandler);
export const post = (...args) => jQuery.post(...setDefaults(args)).fail(loginRedirectHandler);

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (!delay) return setDebouncedValue(value);

    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [value]);

  return debouncedValue;
};

export const ajaxReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH':
      if (state.draw > action.draw) return state;

      return {
        ...state,
        draw: action.draw,
        processing: true,
        error: null,
        data: action.data !== undefined ? action.data : state.data,
      };
    case 'SUCCESS':
      if (state.draw !== action.draw) return state;
      return {
        ...state,
        processing: false,
        data: action.data,
      };
    case 'FAIL':
      if (state.draw !== action.draw) return state;
      return {
        ...state,
        processing: false,
        error: action.error,
      };
    default:
      throw new Error(`Unknown action ${action.type}`);
  }
};

export const useAjaxReducer = (initialData) => {
  return useReducer(ajaxReducer, {
    draw: 0,
    processing: false,
    error: null,
    defaultData: initialData,
    data: initialData,
  });
};

export const ajaxActions = {
  fetch: (draw, data) => ({ type: 'FETCH', draw, data }),
  success: (draw, data) => ({ type: 'SUCCESS', draw, data }),
  fail: (draw, error) => ({ type: 'FAIL', draw, error }),
};

export const useAjax = (initialArgs, initialData, debounceDelay = 100) => {
  const [args, setArgs] = useState({ args: initialArgs, draw: 0 });
  const debounced = useDebounce(args, debounceDelay);

  const setRequest = (args) => {
    setArgs((s) => ({
      args,
      draw: s.draw + 1,
    }));
  };

  const [state, dispatch] = useAjaxReducer(initialData);

  useEffect(() => {
    if (!debounced || !debounced.args) return;

    dispatch(ajaxActions.fetch(debounced.draw));

    let cancelled = false;
    ajax(debounced.args)
      .done((data) => !cancelled && dispatch(ajaxActions.success(debounced.draw, data)))
      .fail(
        (jqXHR, textStatus, errorThrown) =>
          !cancelled && dispatch(ajaxActions.fail(debounced.draw, { jqXHR, textStatus, errorThrown }))
      );

    // Prevent setting state for unmounted components
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  return [state, setRequest];
};
